import React, { useState, useEffect, useRef } from 'react';
import { ArrowPathIcon, PaperAirplaneIcon, FaceSmileIcon } from '@heroicons/react/24/outline';
import { sendMessageStreamApi } from '../../../../services/chatService';
import { chatbotService } from '../../../../services/chatbotService';
import { useAuth } from '../../../../context/AuthContext';
import { getUser } from '../../../../lib/storage';
import { renderChatMarkdown } from '../../../../utility/renderMarkdownText';
import { ActionConfigModal, ActionConfig } from '../../../common/ActionConfigModal';
import { Button } from '../../../ui/Button';
import { SelectField, TextArea } from '../../../ui/Field';
import { Surface } from '../../../ui/Surface';
import { getOrCreateAnonymousId } from '../../../../utils/visitorIdentity';

const defaultSystem = `**Role - Primary Function:**
You are an AI chatbot who helps users with their inquiries, issues and requests. You aim to provide excellent support with clear, concise responses.

**Key Guidelines:**
- Be friendly, professional, and helpful.
- Ask clarifying questions when needed.
- Avoid making assumptions. Do not provide sensitive data.`;

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
};

interface PlaygroundTabProps {
  chatbot: any;
}

const PlaygroundTab: React.FC<PlaygroundTabProps> = ({ chatbot }) => {
  const auth = useAuth();
  const user = auth?.user;
  const token = auth?.user?.token;

  const messagesEndRef: any = useRef(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Settings
  const [model, setModel] = useState('GPT-4o');
  const [temperature, setTemperature] = useState(0);
  const [systemPreset, setSystemPreset] = useState('Base Instructions');
  const [systemInstructions, setSystemInstructions] = useState(defaultSystem);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [shouldFocusInput, setShouldFocusInput] = useState(false);

  // Messages
  const initialMessages: ChatMessage[] = [
    { id: 'm-1', role: 'assistant', content: 'Hi! What can I help you with?' },
  ];
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Action popup state
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [actionInitial, setActionInitial] = useState<Partial<ActionConfig> | undefined>(undefined);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (shouldFocusInput) {
      textareaRef.current?.focus();
      setShouldFocusInput(false);
    }
  }, [shouldFocusInput]);

  const resetChat = () => {
    setMessages(initialMessages);
    setConversationId(undefined);
    setIsLoading(false);
  };

  const handleInput = (e: any) => {
    setInput(e.target.value);
    const textarea: any = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  };

  const resetSystem = () => {
    setSystemPreset('Base Instructions');
    setSystemInstructions(defaultSystem);
  };

  const handleSubmitActionConfig = (cfg: ActionConfig) => {
    // For now just log the configuration. You can persist via API later.
    console.log('Submit Action Config:', cfg);
    setIsActionModalOpen(false);
  };

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed) {
      setShouldFocusInput(true);
      return;
    }
    if (isLoading) {
      setShouldFocusInput(true);
      return;
    }

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: trimmed,
    };
    // Append user message
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    // add loading placeholder message
    const loadingId = `loading-${Date.now()}`;
    const loadingMsg: ChatMessage = {
      id: loadingId,
      role: 'assistant',
      content: '__loading__', // sentinel content to render loader
    };
    setMessages((prev) => [...prev, loadingMsg]);
    setIsLoading(true);

    try {
      if (!chatbot || !user) {
        // remove loading message and set error
        setMessages((prev) =>
          prev.map((m) => (m.id === loadingId ? { ...m, content: 'Error: chatbot or user not available.' } : m))
        );
        setShouldFocusInput(true);
        setIsLoading(false);
        return;
      }

      // Get tenant and user IDs robustly
      const lsTenantId = localStorage.getItem('agentic_tenant_id') || localStorage.getItem('tenant_id');
      const user1 = getUser();
      const envTenantId = user1?.tenantId;

      let storedUser: any = undefined;
      try {
        storedUser = JSON.parse(localStorage.getItem('agentic_user_data') || 'null') || undefined;
      } catch {
        storedUser = undefined;
      }

      const ctxTenantId = (user as any)?.tenantId || (user as any)?.user?.tenantId;
      const storedTenantId = storedUser?.tenantId || storedUser?.user?.tenantId;
      const tenantId = ctxTenantId || lsTenantId || storedTenantId || envTenantId;

      const ctxUserId = (user as any)?.id || (user as any)?.user?.id;
      const storedUserId = storedUser?.id || storedUser?.user?.id;
      const finalUserId = ctxUserId || storedUserId;

      console.log('Send Message Debug:', {
        userTenantId: (user as any)?.tenantId || (user as any)?.user?.tenantId,
        lsTenantId,
        envTenantId,
        finalTenantId: tenantId,
        chatbotId: chatbot.id,
        userId: finalUserId,
        message: trimmed,
      });

      // The chat endpoint STREAMS (SSE: `data: {"type":"content"|"complete", ...}`),
      // exactly like the live chatbot widget. Consume the stream and update the
      // assistant message incrementally so the Playground behaves like production.
      const response = await sendMessageStreamApi({
        chatbotId: chatbot.id,
        tenantId: tenantId,
        userId: finalUserId,
        message: trimmed,
        conversationId,
        // Same per-browser visitor id the public widget sends, so Playground tests exercise the
        // memory platform exactly like a real visitor (recognised again in a new conversation).
        anonymousId: getOrCreateAnonymousId(),
      });

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let accumulated = '';
      let buffer = '';
      let receivedFirstChunk = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (!value) continue;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts[parts.length - 1]; // keep the trailing partial frame

        for (let i = 0; i < parts.length - 1; i++) {
          const line = parts[i].trim();
          if (!line.startsWith('data: ')) continue;
          try {
            const data: any = JSON.parse(line.replace('data: ', ''));
            if (data.type === 'content') {
              if (!receivedFirstChunk) { receivedFirstChunk = true; accumulated = ''; }
              accumulated += data.content ?? '';
              setMessages((prev) =>
                prev.map((m) => (m.id === loadingId ? { ...m, role: 'assistant', content: accumulated } : m))
              );
            } else if (data.type === 'complete') {
              accumulated = data.content || accumulated;
              setMessages((prev) =>
                prev.map((m) => (m.id === loadingId ? { ...m, role: 'assistant', content: accumulated } : m))
              );
              if (!conversationId && data.conversationId) {
                setConversationId(data.conversationId);
              }
            }
          } catch {
            /* ignore malformed SSE frame */
          }
        }
      }

      // No content ever arrived — show a graceful fallback instead of a stuck loader.
      if (!accumulated) {
        setMessages((prev) =>
          prev.map((m) => (m.id === loadingId ? { ...m, role: 'assistant', content: 'Sorry, no response.' } : m))
        );
      }
      setShouldFocusInput(true);
    } catch (err) {
      // replace loading message with error message
      setMessages((prev) =>
        prev.map((m) =>
          m.id === loadingId
            ? { id: `e-${Date.now() + 2}`, role: 'assistant', content: 'Error sending message. Please try again.' }
            : m
        )
      );
      setShouldFocusInput(true);
    } finally {
      setShouldFocusInput(true);
      setIsLoading(false);
    }
  };

  // Simple three-dot loader component (CSS animation inside component)
  const ThreeDotLoader: React.FC = () => (
    <span className="inline-flex items-center space-x-1">
      <style>
        {`
        @keyframes dotBounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.6; }
          40% { transform: translateY(-6px); opacity: 1; }
        }
        .dot {
          width: 6px;
          height: 6px;
          border-radius: 9999px;
          background: currentColor;
          display: inline-block;
        }
        .dot1 { animation: dotBounce 1s infinite; animation-delay: 0s; }
        .dot2 { animation: dotBounce 1s infinite; animation-delay: 0.15s; }
        .dot3 { animation: dotBounce 1s infinite; animation-delay: 0.3s; }
      `}
      </style>
      <span className="dot dot1" />
      <span className="dot dot2" />
      <span className="dot dot3" />
    </span>
  );

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left Controls */}
        <div className="xl:col-span-1 space-y-5">
          {/* Playground Header */}
          <Surface tone="plain" className="border rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-agentic-text-primary">Playground</h3>
            </div>
            <div className="mt-3 flex items-center gap-3 rounded-lg border p-3">
              <span className="inline-flex items-center">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 mr-2" />
                <span className="text-sm font-medium text-emerald-700">Trained</span>
              </span>
              <span className="text-xs text-agentic-text-secondary">Last trained just now</span>
            </div>
          </Surface>

          {/* System Instructions */}
          <Surface tone="plain" className="border rounded-2xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-agentic-text-primary">
                Instructions (System prompt)
              </label>
              <div className="flex items-center gap-2">
                <SelectField tone="default"
                  value={systemPreset}
                  onChange={(e) => setSystemPreset(e.target.value)}
                  className="px-2 py-1.5 rounded-lg border text-sm"
                >
                  <option>Base Instructions</option>
                  <option>Support Assistant</option>
                  <option>Sales Assistant</option>
                </SelectField>
                <Button tone="outline"
                  type="button"
                  className="p-2 rounded-lg border"
                  onClick={resetSystem}
                  aria-label="Reset system prompt"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <TextArea tone="default-focus-primary"
              rows={10}
              value={systemInstructions}
              onChange={(e) => setSystemInstructions(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border focus:ring-2"
            />
          </Surface>
        </div>

        {/* Right Chat Preview */}
        <div className="xl:col-span-2">
          <div className="w-full max-w-[840px] mx-auto my-6 bg-white/80 backdrop-blur rounded-2xl border shadow-sm overflow-hidden flex flex-col min-h-[680px]">
            {/* Chat Header */}
            <div className="h-12 bg-gradient-to-r from-agentic-primary-blue to-agentic-primary-blue-light text-white flex items-center justify-between px-4">
              <div className="font-medium">Live Chat</div>
              <Button tone="custom"
                onClick={resetChat}
                className="p-1 rounded hover:bg-white/20"
                aria-label="Reset chat"
              >
                <ArrowPathIcon className="h-5 w-5" />
              </Button>
            </div>

            {/* Messages */}
            <div
              className="overflow-y-auto p-4 space-y-3 bg-c-f7f9fc"
              style={{ height: '700px' }}
            >
              {messages.map((m) => {
                const isUser = m.role === 'user';
                const isLoadingMsg = m.content === '__loading__';
                return (
                  <div key={m.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm shadow ${isUser
                          ? 'bg-gradient-to-r from-agentic-primary-blue to-agentic-primary-blue-light text-white'
                          : 'bg-agentic-gray-50 text-agentic-text-primary border'
                        }`}
                    >
                      <div className="message-content">
                        {isLoadingMsg ? (
                          // render three-dot loader for loading messages
                          <div className="flex items-center gap-2">
                            {/* <span className="text-agentic-text-secondary text-sm">Thinking</span> */}
                            <ThreeDotLoader />
                          </div>
                        ) : (
                          renderChatMarkdown(m.content)
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Powered by */}
            <div className="px-4 pb-1 text-center text-[11px] text-agentic-text-secondary/80">
              {/* Powered by text if needed */}
            </div>

            {/* Input */}
            <div className="p-3 border-t">
              <Surface tone="plain" className="flex items-end gap-2 border rounded-xl px-3 py-3">
                <Button tone="muted-text"
                  className="p-1"
                  type="button"
                  disabled={isLoading}
                >
                  <FaceSmileIcon className="h-5 w-5" />
                </Button>
                <TextArea tone="transparent"
                  ref={textareaRef}
                  value={input}
                  onChange={handleInput}
                  rows={1}
                  placeholder="Message..."
                  className="flex-1 resize-none outline-none text-base overflow-auto"
                  style={{ maxHeight: '120px' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  disabled={isLoading}
                />
                <Button tone="custom"
                  onClick={sendMessage}
                  className={`p-2 rounded-md text-white ${isLoading ? 'bg-gray-300 cursor-not-allowed' : 'bg-agentic-primary-blue hover:bg-agentic-primary-blue/90'}`}
                  disabled={isLoading}
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                </Button>
              </Surface>
            </div>
          </div>
        </div>
      </div>
      {/* Action Config Modal */}
      {/* <ActionConfigModal
        isOpen={isActionModalOpen}
        onClose={() => setIsActionModalOpen(false)}
        initialConfig={actionInitial}
        onSubmit={handleSubmitActionConfig}
      /> */}
    </div>
  );
};

export default PlaygroundTab;
